import { useEffect, useRef } from 'react';
import { NR2J_APPROVALS_CHANGED_EVENT } from '../utils/nr2jApprovals';
import { NR2J_DATA_CHANGED_EVENT } from '../utils/nr2jRealtime';

/**
 * Polling court + focus / visibility + events locaux.
 * Réutilise le bus d’événements existant (pas d’architecture realtime parallèle).
 *
 * @param {() => void} callback
 * @param {{ enabled?: boolean, intervalMs?: number, runOnMount?: boolean }} [opts]
 *   runOnMount=false : éviter la course avec un load() initial (Journal / listes).
 */
export function useNr2jAutoRefresh(callback, {
  enabled = true,
  intervalMs = 12000,
  runOnMount = true,
} = {}) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      try {
        callbackRef.current?.();
      } catch {
        /* silent */
      }
    };

    if (runOnMount) run();
    const interval = setInterval(run, intervalMs);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };
    const onFocus = () => run();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener(NR2J_APPROVALS_CHANGED_EVENT, run);
    window.addEventListener(NR2J_DATA_CHANGED_EVENT, run);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(NR2J_APPROVALS_CHANGED_EVENT, run);
      window.removeEventListener(NR2J_DATA_CHANGED_EVENT, run);
    };
    // callback volontairement hors deps (ref) — évite de relancer l’effet à chaque render filters.
  }, [enabled, intervalMs, runOnMount]);
}
